import { styled } from '@mui/material/styles';
import { Button } from '@mui/material';

export const PageHeaderContainer = styled('div')(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

export const SectionTitle = styled('h1')({
    fontSize: '1.8rem',
    marginBottom: '0.5rem',
    fontWeight: 500,
    color: '#333',
});

export const ComparisonSection = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    marginTop: '20px',
    marginBottom: '20px',
});

export const StyledButton = styled(Button)( ({theme}) => ({
    backgroundColor: theme.palette.secondary.main
}))

export const DocumentSelectionArea = styled('div')({
    display: 'flex',
    gap: '20px',
    marginBottom: '20px',

    '@media (max-width: 768px)': {
        flexDirection: 'column',
    },
});

export const FileInputContainer = styled('div')({
    flex: 1,
    border: '1px solid rgba(0, 0, 0, 0.1)',
    borderRadius: '5px',
    padding: '20px',
    backgroundColor: '#fff',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',

    '& h3': {
        marginTop: 0,
        marginBottom: '5px',
        fontWeight: 500,
        textAlign: 'center',
    },
    '& p': {
        fontSize: '0.9rem',
        color: 'rgba(0, 0, 0, 0.6)',
        textAlign: 'center',
        marginBottom: '15px',
    },
});

export const FileNameDisplay = styled('div')({
    marginTop: '10px',
    fontSize: '0.9rem',
    color: 'rgba(0, 0, 0, 0.8)',
    fontStyle: 'italic',
    textAlign: 'center',
    wordBreak: 'break-word',
});

export const ButtonGroup = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    gap: '15px',
    margin: '20px 0',
});

export const ComparisonResults = styled('div')({
    marginTop: '20px',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    borderRadius: '5px',
    padding: '20px',
    backgroundColor: '#fff',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
});

export const ComparisonTabs = styled('div')({
    display: 'flex',
    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
    marginBottom: '20px',
});

export const ComparisonTab = styled('div', {
    shouldForwardProp: (prop) => prop !== 'isActive'
})(({ isActive, theme }) => ({
    padding: '10px 20px',
    cursor: 'pointer',
    fontWeight: isActive ? 500 : 'normal',
    borderBottomWidth: isActive ? '2px' : '0',
    borderBottomStyle: isActive ? 'solid' : 'none',
    borderBottomColor: isActive ? theme.palette.secondary.main : 'transparent',
    color: isActive ? theme.palette.secondary.main : 'inherit',
}));

export const HighlightDiff = styled('span')({
    backgroundColor: 'rgba(255, 100, 100, 0.2)',
    textDecoration: 'line-through',
    padding: '2px 0',
});

export const AdditionDiff = styled('span')({
    backgroundColor: 'rgba(100, 200, 100, 0.2)',
    padding: '2px 0',
});

export const DiffView = styled('div')({
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    lineHeight: 1.5,
    '& .addition': {
        backgroundColor: 'rgba(100, 200, 100, 0.3)',
        padding: '1px 0',
        borderRadius: '2px',
    },
    '& .deletion': {
        backgroundColor: 'rgba(255, 100, 100, 0.3)',
        textDecoration: 'line-through',
        padding: '1px 0',
        borderRadius: '2px',
    },
});

export const SideBySideView = styled('div')({
    display: 'flex',
    gap: '20px',

    '@media (max-width: 768px)': {
        flexDirection: 'column',
    },
});

export const SideBySideDocument = styled('div')({
    flex: 1,
    border: '1px solid rgba(0, 0, 0, 0.1)',
    borderRadius: '4px',
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    maxHeight: '600px',
    overflowY: 'auto',
    fontFamily: 'monospace',
    fontSize: '0.9rem',
    lineHeight: 1.5,
});

export const LineContainer = styled('div')({
    display: 'flex',
    borderTop: '1px solid rgba(0, 0, 0, 0.05)',
    '&:first-of-type': {
        borderTop: 'none',
    },
    minHeight: '1.5em',
});

export const LineNumber = styled('div')(() => ({
    padding: '0 10px',
    textAlign: 'right',
    color: 'rgba(0, 0, 0, 0.4)',
    backgroundColor: 'rgba(0, 0, 0, 0.03)',
    userSelect: 'none',
    minWidth: '40px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
}));

export const LineContent = styled('div')({
    flex: 1,
    padding: '0 10px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',

    '& .addition': {
        backgroundColor: 'rgba(100, 200, 100, 0.4)',
        borderRadius: '2px',
    },
    '& .deletion': {
        backgroundColor: 'rgba(255, 100, 100, 0.4)',
        textDecoration: 'line-through',
        borderRadius: '2px',
    },
    '& .diff-placeholder': {
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        minHeight: '1.5em',
        display: 'block',
    },
    '&.empty-line': {
        minHeight: '1.5em',
        display: 'block',
    },
});

export const SummaryStats = styled('div')({
    display: 'flex',
    gap: '20px',
    marginBottom: '20px',
    flexWrap: 'wrap',
});

export const StatCard = styled('div')({
    flex: 1,
    minWidth: '150px',
    padding: '15px',
    borderRadius: '4px',
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    textAlign: 'center',
});

export const StatValue = styled('div')({
    fontSize: '2rem',
    fontWeight: 500,
    color: '#C5AB6E',
});

export const StatLabel = styled('div')({
    fontSize: '0.9rem',
    color: 'rgba(0, 0, 0, 0.5)',
    marginTop: '5px',
});
